import React, { useState, useEffect, useRef, useCallback } from 'react';

export default function FloatingBlogButton({
  onNavigate,
  label = 'Blog',
  lastBlogTimestamp,
  unseenBlogCount = 0,
}) {
  const SIZE        = 56;
  const EDGE_PAD    = 14;
  const DRAG_THRESH = 6;
  const PEEK        = 12; // how many px show past the edge when snapped

  // Always starts snapped LEFT, peeking from the left edge
  // pos.x is only used during active drag; displayX handles the snapped visual
  const [pos,         setPos]         = useState({ x: EDGE_PAD, y: window.innerHeight * 0.55 });
  const [snappedSide, setSnappedSide] = useState('left');
  const [dragging,    setDragging]    = useState(false);
  const [visible,     setVisible]     = useState(false);
  const [pulse,       setPulse]       = useState(true);
  const [overTrash,   setOverTrash]   = useState(false);
  const [hidden,      setHidden]      = useState(false);

  const drag = useRef({ active:false, startX:0, startY:0, originX:0, originY:0, moved:false });

  // Re-show on new blog post
  useEffect(() => {
    if (lastBlogTimestamp) setHidden(false);
  }, [lastBlogTimestamp]);

  useEffect(() => { const t = setTimeout(() => setVisible(true), 200); return () => clearTimeout(t); }, []);
  useEffect(() => { const t = setTimeout(() => setPulse(false),  4000); return () => clearTimeout(t); }, []);

  // ── Snap ───────────────────────────────────────────────────────────────────
  const snapToEdge = useCallback((rawX, rawY) => {
    const vw = window.innerWidth, vh = window.innerHeight;
    const clampedY = Math.min(Math.max(rawY, EDGE_PAD), vh - SIZE - EDGE_PAD);
    const side = rawX + SIZE / 2 <= vw / 2 ? 'left' : 'right';
    setSnappedSide(side);
    setPos({ x: side === 'left' ? EDGE_PAD : vw - SIZE - EDGE_PAD, y: clampedY });
  }, []);

  // ── Trash zone ─────────────────────────────────────────────────────────────
  const TRASH_W = 120, TRASH_H = 76;
  const getTrashRect = useCallback(() => ({
    left:   window.innerWidth  / 2 - TRASH_W / 2,
    top:    window.innerHeight - TRASH_H - 20,
    right:  window.innerWidth  / 2 + TRASH_W / 2,
    bottom: window.innerHeight - 20,
  }), []);

  const checkOverTrash = useCallback((x, y) => {
    const r = getTrashRect();
    return (x + SIZE/2) >= r.left && (x + SIZE/2) <= r.right
        && (y + SIZE/2) >= r.top  && (y + SIZE/2) <= r.bottom;
  }, [getTrashRect]);

  // ── Pointer ────────────────────────────────────────────────────────────────
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    // When they start dragging, anchor from the current visual snapped position
    const startX = snappedSide === 'left' ? -PEEK : window.innerWidth - SIZE + PEEK;
    drag.current = {
      active:true, moved:false,
      startX:e.clientX, startY:e.clientY,
      originX:startX,   originY:pos.y,
    };
  };

  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    if (!drag.current.moved && Math.hypot(dx, dy) < DRAG_THRESH) return;
    drag.current.moved = true;
    setDragging(true);
    setPulse(false);
    const newX = Math.min(Math.max(drag.current.originX + dx, -PEEK), window.innerWidth - SIZE + PEEK);
    const newY = Math.min(Math.max(drag.current.originY + dy, 0), window.innerHeight - SIZE);
    setPos({ x: newX, y: newY });
    setOverTrash(checkOverTrash(newX, newY));
  };

  const onPointerUp = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    if (!drag.current.moved) {
      setDragging(false); setOverTrash(false);
      onNavigate && onNavigate('blog');
      return;
    }
    if (checkOverTrash(pos.x, pos.y)) {
      setDragging(false); setOverTrash(false);
      setHidden(true);
      return;
    }
    snapToEdge(pos.x, pos.y);
    setDragging(false); setOverTrash(false);
  };

  useEffect(() => {
    const h = () => snapToEdge(pos.x, pos.y);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [pos, snapToEdge]);

  if (hidden) return null;

  // When not dragging, always show peeking from the snapped edge
  const displayX = dragging
    ? pos.x
    : snappedSide === 'left'
      ? -PEEK                              // left edge peek
      : window.innerWidth - SIZE + PEEK;   // right edge peek

  const tr = getTrashRect();

  return (
    <>
      <style>{`
        @keyframes fab-pulse {
          0%,100% { box-shadow:0 0 0 0    rgba(212,160,23,.6); }
          60%      { box-shadow:0 0 0 14px rgba(212,160,23,0); }
        }
        @keyframes fab-ring {
          0%   { opacity:.7; transform:scale(1); }
          100% { opacity:0;  transform:scale(2); }
        }
        @keyframes fab-fadein {
          from { opacity:0; transform:scale(.5) translateX(-10px); }
          to   { opacity:1; transform:scale(1)  translateX(0); }
        }
        @keyframes trash-in {
          from { opacity:0; transform:translateY(24px) scale(.8); }
          to   { opacity:1; transform:translateY(0)    scale(1); }
        }
        @keyframes fab-dismiss {
          0%   { opacity:1; transform:scale(1); }
          50%  { opacity:.6; transform:scale(1.2); }
          100% { opacity:0; transform:scale(0); }
        }
        @keyframes fab-badge-pop {
          from { transform:scale(0); opacity:0; }
          to   { transform:scale(1); opacity:1; }
        }

        .fab-wrap {
          position:fixed; z-index:9999;
          user-select:none; touch-action:none;
          will-change:left,top;
        }
        .fab-btn {
          width:${SIZE}px; height:${SIZE}px; border-radius:50%;
          background:linear-gradient(145deg,#0d3318,#1c4f35);
          border:2.5px solid #d4a017;
          display:flex; flex-direction:column;
          align-items:center; justify-content:center;
          cursor:grab;
          box-shadow:0 4px 20px rgba(10,46,20,.4), 0 1px 4px rgba(0,0,0,.25),
                     inset 0 1px 0 rgba(255,255,255,.08);
          transition:border-color .2s, background .2s, box-shadow .2s, transform .15s;
        }
        .fab-wrap.dragging .fab-btn  { cursor:grabbing; transform:scale(1.05); }
        .fab-wrap.on-trash .fab-btn  {
          border-color:#ff4a4a;
          background:linear-gradient(145deg,#2e0a0a,#4a1515);
          box-shadow:0 0 0 5px rgba(255,74,74,.25), 0 8px 28px rgba(255,74,74,.2);
          transform:scale(1.1);
        }
        .fab-icon { color:#d4a017; margin-bottom:2px; pointer-events:none; transition:color .2s; }
        .fab-wrap.on-trash .fab-icon { color:#ff4a4a; }
        .fab-label {
          font-family:'Space Grotesk','Manrope',sans-serif;
          font-size:8.5px; font-weight:700; letter-spacing:.1em;
          color:rgba(255,255,255,.8); text-transform:uppercase;
          pointer-events:none; line-height:1;
        }

        .fab-ring {
          position:fixed; border-radius:50%;
          border:2px solid rgba(212,160,23,.45);
          pointer-events:none; z-index:9998;
          animation:fab-ring 1.8s ease-out infinite;
        }

        .fab-trash {
          position:fixed; z-index:9997; pointer-events:none;
          display:flex; flex-direction:column;
          align-items:center; justify-content:center; gap:4px;
          border-radius:16px;
          border:1.5px dashed rgba(255,255,255,.2);
          background:rgba(15,15,15,.45);
          backdrop-filter:blur(10px); -webkit-backdrop-filter:blur(10px);
          animation:trash-in .22s cubic-bezier(.34,1.56,.64,1) both;
          transition:border-color .18s, background .18s;
        }
        .fab-trash.on-trash {
          border-color:rgba(255,74,74,.55);
          background:rgba(255,20,20,.12);
        }
        .fab-trash-lbl {
          font-family:'Space Grotesk',sans-serif;
          font-size:8.5px; font-weight:600; letter-spacing:.08em;
          text-transform:uppercase; color:rgba(255,255,255,.35);
          transition:color .18s;
        }
        .fab-trash.on-trash .fab-trash-lbl { color:rgba(255,120,120,.9); }
      `}</style>

      {/* Pulse ring – tracks the FAB */}
      {pulse && !dragging && (
        <div className="fab-ring" style={{
          width:SIZE+18, height:SIZE+18,
          left:displayX - 9, top:pos.y - 9,
          transition:'left .38s cubic-bezier(.34,1.56,.64,1), top .18s ease',
        }}/>
      )}

      {/* Trash zone – only while dragging */}
      {dragging && (
        <div className={`fab-trash${overTrash ? ' on-trash' : ''}`} style={{
          left:tr.left, top:tr.top, width:TRASH_W, height:TRASH_H,
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke={overTrash ? 'rgba(255,110,110,.9)' : 'rgba(255,255,255,.4)'}
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
            style={{transition:'stroke .18s'}}>
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6l-1 14H6L5 6"/>
            <path d="M10 11v6M14 11v6"/>
            <path d="M9 6V4h6v2"/>
          </svg>
          <span className="fab-trash-lbl">dismiss</span>
        </div>
      )}

      {/* FAB wrapper – handles position */}
      <div
        className={`fab-wrap${dragging ? ' dragging' : ''}${overTrash ? ' on-trash' : ''}`}
        style={{
          left: displayX,
          top:  pos.y,
          opacity: visible ? 1 : 0,
          transition: dragging
            ? 'opacity .5s'
            : 'left .4s cubic-bezier(.34,1.56,.64,1), top .2s ease, opacity .5s',
          animation: visible && !dragging && !pulse
            ? 'fab-fadein .5s cubic-bezier(.34,1.56,.64,1) both'
            : undefined,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        title="Read our market intelligence blog"
      >
        <div
          className="fab-btn"
          style={pulse && !dragging
            ? { animation:'fab-pulse 2s ease-in-out infinite' }
            : undefined}
        >
          <svg className="fab-icon" width="17" height="17" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            <line x1="9" y1="7" x2="15" y2="7"/>
            <line x1="9" y1="11" x2="13" y2="11"/>
          </svg>
          <span className="fab-label">{label}</span>
          {/* New blog badge */}
          {unseenBlogCount > 0 && !dragging && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              minWidth: 18, height: 18, borderRadius: 9,
              background: '#ef4444',
              border: '2px solid #0a2e14',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 800, color: '#fff',
              fontFamily: 'Space Grotesk, sans-serif',
              padding: unseenBlogCount > 9 ? '0 4px' : 0,
              lineHeight: 1,
              animation: 'fab-badge-pop 0.4s cubic-bezier(.34,1.56,.64,1) both',
              pointerEvents: 'none',
            }}>
              {unseenBlogCount > 9 ? '9+' : unseenBlogCount}
            </span>
          )}
        </div>
      </div>
    </>
  );
}